import { useEffect, useRef } from "react";

import { getApiBase } from "@/api";

/**
 * Tab registry stored in localStorage so we can tell when the *last* app tab
 * closes. Each tab heartbeats its id; stale entries (>5s) are pruned.
 */
const TAB_STORAGE_KEY = "billforge_open_tabs";
const HEARTBEAT_MS = 2_000;
const STALE_MS = 5_000;

function readTabs(): Record<string, number> {
  try {
    const raw = localStorage.getItem(TAB_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== "object") return {};
    return parsed as Record<string, number>;
  } catch {
    return {};
  }
}

function writeTabs(tabs: Record<string, number>): void {
  localStorage.setItem(TAB_STORAGE_KEY, JSON.stringify(tabs));
}

function pruneStale(tabs: Record<string, number>): Record<string, number> {
  const now = Date.now();
  const out: Record<string, number> = {};
  for (const [id, ts] of Object.entries(tabs)) {
    if (now - ts <= STALE_MS) out[id] = ts;
  }
  return out;
}

function unregisterTab(tabId: string): boolean {
  const tabs = pruneStale(readTabs());
  delete tabs[tabId];
  const remaining = Object.keys(tabs);
  if (remaining.length === 0) {
    localStorage.removeItem(TAB_STORAGE_KEY);
    return true;
  }
  writeTabs(tabs);
  return false;
}

function sendCloseLogout(): void {
  const url = `${getApiBase()}/auth/logout`;
  // keepalive lets the request finish while the tab is unloading.
  void fetch(url, {
    method: "POST",
    credentials: "include",
    keepalive: true,
    headers: { "content-type": "application/json" },
  }).catch(() => {
    /* best-effort */
  });
}

/**
 * When the user closes the browser (session cookie) or the last open app tab,
 * revoke the server session so the next visit requires login again.
 *
 * Reload/back-forward is ignored so F5 / Ctrl+R does not sign the user out.
 */
export function useSessionCloseLogout(enabled: boolean): void {
  const tabIdRef = useRef<string>(
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : `tab-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  );
  const reloadIntentRef = useRef(false);

  useEffect(() => {
    if (!enabled) return;

    const tabId = tabIdRef.current;
    reloadIntentRef.current = false;

    const heartbeat = () => {
      const tabs = pruneStale(readTabs());
      tabs[tabId] = Date.now();
      writeTabs(tabs);
    };

    heartbeat();
    const heartbeatTimer = window.setInterval(heartbeat, HEARTBEAT_MS);

    const markReloadIntent = () => {
      reloadIntentRef.current = true;
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "F5") {
        markReloadIntent();
        return;
      }
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "r") {
        markReloadIntent();
      }
    };

    // Navigation API covers toolbar reload in Chromium-based browsers.
    const nav = (
      window as Window & {
        navigation?: {
          addEventListener: (
            type: "navigate",
            listener: (event: { navigationType?: string }) => void,
          ) => void;
          removeEventListener: (
            type: "navigate",
            listener: (event: { navigationType?: string }) => void,
          ) => void;
        };
      }
    ).navigation;

    const onNavigate = (event: { navigationType?: string }) => {
      if (event.navigationType === "reload") markReloadIntent();
    };
    nav?.addEventListener("navigate", onNavigate);

    const onPageHide = (event: PageTransitionEvent) => {
      // bfcache restore — not a real close.
      if (event.persisted) return;
      if (reloadIntentRef.current) {
        reloadIntentRef.current = false;
        unregisterTab(tabId);
        return;
      }

      const isLastTab = unregisterTab(tabId);
      if (isLastTab) sendCloseLogout();
    };

    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("pagehide", onPageHide);

    return () => {
      window.clearInterval(heartbeatTimer);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("pagehide", onPageHide);
      nav?.removeEventListener("navigate", onNavigate);
      unregisterTab(tabId);
    };
  }, [enabled]);
}
